import React from 'react';
import { Box, costFormater, formatNumber, InfoOutlineIcon, SimpleHint, Text } from '@common-fe/common-fe';

import { DeductibleTrackingHint, StatisticsItem } from './components';
import { useGetEmployerStatisticsQuery } from './queries';

const Delimiter = () => {
  return (
    <div>
      <Box
        width="2px"
        margin={{ horizontal: 'spacing16' }}
        round="smallRound"
        background="border2"
        height={{ min: '100%' }}
      />
    </div>
  );
};

interface Props {
  isHSA?: boolean;
  planId: string;
  isLoading?: boolean;
}
const EmployerStatisticsContainer: React.FC<Props> = ({ planId, isHSA, isLoading }) => {
  const { isLoading: isStatisticLoading, data } = useGetEmployerStatisticsQuery({
    planId,
    isHSA,
    enabled: !isLoading,
  });
  if (isHSA) {
    return (
      <Box direction="row" pad={{ vertical: 'spacing24' }} data-testid="employer-statistics">
        <StatisticsItem
          isLoading={isStatisticLoading}
          title="Active Enrollments"
          hint="Total plans canceled within the plan year"
        >
          {formatNumber(data?.activeEnrollments || 0)}
        </StatisticsItem>
        <Delimiter />
        <StatisticsItem
          isLoading={isStatisticLoading}
          title="Unenrolled HSAs"
          subfieldTitle="Accounts"
          hint="Employees with an open HSA but are not currently enrolled"
        >
          {formatNumber(data?.unenrolledHSAs || 0)}
        </StatisticsItem>
        <Delimiter />
        <StatisticsItem
          isLoading={isStatisticLoading}
          title="Plan Year Contributions"
          subfieldTitle="YTD"
          hint="Sum of all payroll contributions this plan year"
        >
          {costFormater(data?.planYearContributions || 0)}
        </StatisticsItem>
        {data?.investmentBalance !== null && (
          <>
            <Delimiter />
            <StatisticsItem
              isLoading={isStatisticLoading}
              title="Investments"
              subfieldTitle="Participants / Balance"
              hintNode={
                <Box>
                  <Text size="small">Participants: total number of individuals enrolled in investments</Text>
                  <Text></Text>
                  <Text size="small" margin={{ top: 'spacing12' }}>
                    Balance: Sum of all dollars invested
                  </Text>
                </Box>
              }
            >
              {`${formatNumber(data?.investmentParticipants || 0)} / ${costFormater(data?.investmentBalance || 0)}`}
            </StatisticsItem>
          </>
        )}

        <Delimiter />
        <StatisticsItem
          isLoading={isStatisticLoading}
          title="Total Elections"
          subfieldTitle="Active and canceled"
          hint="Total plan year elections including active and canceled plans"
        >
          {costFormater(data?.totalElections || 0)}
        </StatisticsItem>
      </Box>
    );
  }
  return (
    <Box direction="row" pad={{ vertical: 'spacing24' }} data-testid="employer-statistics">
      <StatisticsItem
        isLoading={isStatisticLoading}
        title="Active Enrollments"
        subfieldTitle={`${data?.canceledEnrollments || 0} canceled`}
        hint="Total plans canceled within the plan year"
      >
        <Box direction="row" align="center">
          <Text size="xxlarge" margin={{ right: '8px' }}>
            {formatNumber(data?.activeEnrollments)}
          </Text>
          {!data?.plans?.length && !data?.limitedUseCount ? null : (
            <SimpleHint
              isFixed
              content={<DeductibleTrackingHint value={data?.plans} limitedUseCount={data?.limitedUseCount} />}
            >
              <Box direction="row" align="center">
                <InfoOutlineIcon size="20px" color="iconSecondary" />
              </Box>
            </SimpleHint>
          )}
        </Box>
      </StatisticsItem>
      <Delimiter />
      <StatisticsItem
        isLoading={isStatisticLoading}
        title="Plan Year Contributions"
        subfieldTitle="YTD"
        hint="Sum of all payroll contributions this plan year"
      >
        {costFormater(data?.planYearContributions || 0)}
      </StatisticsItem>
      <Delimiter />
      <StatisticsItem
        isLoading={isStatisticLoading}
        title="Total Elections"
        subfieldTitle="Active and canceled"
        hint="Total plan year elections including active and canceled plans"
      >
        {costFormater(data?.totalElections || 0)}
      </StatisticsItem>
      {data?.rolloverIn !== null && (
        <>
          <Delimiter />
          <StatisticsItem
            isLoading={isStatisticLoading}
            title="Rollover"
            subfieldTitle="In / Out"
            hintNode={
              <Box>
                <Text textAlign="center" size="small">
                  Rolled in: the amount rolled in to this plan from the prior plan year.
                </Text>
                <Text></Text>
                <Text textAlign="center" size="small" margin={{ top: 'spacing12' }}>
                  Rolled out: the amount rolled out of this plan in to the next plan year
                </Text>
              </Box>
            }
          >
            {`${costFormater(data?.rolloverIn || 0)} / ${costFormater(data?.rolloverOut || 0)}`}
          </StatisticsItem>
        </>
      )}
    </Box>
  );
};

export default EmployerStatisticsContainer;
